/* eslint-disable */
// src/components/VisitTransactionDetails/visitTransactionDetails.js

import React, { useCallback, useMemo, useState } from "react";
import { Box } from "@mui/material";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import PropTypes from "prop-types";
import { nfRoundedDynamic, moneyFmtRoundedDynamic } from "utils/formatters";
import { formatPrevCollectionValue } from "utils/visitDisplayFormatters";
import buildVisitTable from "tables/buildVisitTables";
import useNotify from "hooks/useNotify";
import { machineTypeLabel, machineTypeGender } from "utils/machineLabels";
import { buildVisitPdfFilename } from "utils/shareReport";
import { buildCollectionA4Model, generateCollectionA4Pdf } from "utils/collectionA4";
import ShareButton from "components/common/ShareButton";
import { hasHistoricalRent } from "utils/rent";
import {
  getMachineTypeName,
  sortLocalMachinesFixed,
  sortTypeEntries,
} from "utils/localMachineOrder";
import {
  isInsertedInCurrentVisitNoticeRow,
  isPersistedCollectedMachine,
} from "utils/visitCollectionState";
import resolveUncollectedPreviousPresentation from "utils/uncollectedPreviousPresentation";
import { hasMachineOperatorFormula } from "utils/machineFormula";

/* =============== Skin compartido ================= */
import {
  CURVE,
  RADII,
  TYPE_COLORS,
  TypeBlocksGlobalStyles,
  getTypeStyle,
  getInnerPanelStyle,
  getTableSkin,
} from "ui/skins/typeBlocks";

/** ================= Curvatura + alturas ================= */
const META_H = 46; // ALTURA de barra y botón (px)
const SHARE_W = 140; // ancho reservado para el botón (px)
const TYPE_TITLE_FONT = 16;

/** Botón flotante (fuera del printable) */
const shareFloaterSx = {
  position: "absolute",
  top: 0,
  right: 10,
  zIndex: 5,
  "& .MuiButton-root": {
    height: META_H,
    minHeight: META_H,
    paddingInline: 16,
    borderRadius: `${CURVE}px !important`,
  },
};

// Capitaliza 1ª letra
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const rentedWord = (count, gender = "f") => {
  const base = gender === "m" ? "alquilado" : "alquilada";
  return cap(base + (Number(count) === 1 ? "" : "s"));
};

const percentageFmt = new Intl.NumberFormat("es-UY", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 20,
});

const formatPercentage = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? percentageFmt.format(numericValue) : String(value);
};

const formatVisitDateDDMMYYYY = (visit) => {
  const uruguayDate = String(visit?.DateUY ?? "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (uruguayDate) return `${uruguayDate[3]}/${uruguayDate[2]}/${uruguayDate[1]}`;

  const dateLike = visit?.DateTime ?? visit?.Date ?? null;
  if (!dateLike) return "—";
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-UY", {
    timeZone: "America/Montevideo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

function VisitTransactionDetails({
  visit,
  visitWithCollection,
  machines = [],
  lastLocalVisitBeforeId = null,
  lastLocalVisitBeforeDate = null,
  showPreviousLocalCollectionInfo = false,
  local = null,
  person = null,
  a4DataReady = true,
  collectionDetailPresentation = false,
}) {
  const { notifyError } = useNotify();
  const [isGeneratingA4, setIsGeneratingA4] = useState(false);
  const [
    counterVerificationExpandedByLocalMachineId,
    setCounterVerificationExpandedByLocalMachineId,
  ] = useState({});
  const isCounterVerificationExpanded = useCallback(
    (localMachineId) =>
      Boolean(counterVerificationExpandedByLocalMachineId?.[Number(localMachineId)]),
    [counterVerificationExpandedByLocalMachineId]
  );
  const toggleCounterVerificationExpanded = useCallback((localMachineId) => {
    const key = Number(localMachineId);
    setCounterVerificationExpandedByLocalMachineId((prev) => ({
      ...prev,
      [key]: !prev?.[key],
    }));
  }, []);
  const NEG_COLOR = "#B31D28";
  const negStyle = (n) => (Number.isFinite(Number(n)) && Number(n) < 0 ? { color: NEG_COLOR } : {});
  const toIdOrNull = (value) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const formatDateDDMM = (dateLike) => {
    if (!dateLike) return null;
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return null;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}`;
  };
  const formatDateDDMMYYYY = (dateLike) => {
    if (!dateLike) return null;
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return null;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const getPrevDateSuffix = (machineRow) => {
    const prevId = toIdOrNull(machineRow?.PrevVisitId);
    const lastBeforeId = toIdOrNull(lastLocalVisitBeforeId);
    if (!prevId || !lastBeforeId || prevId === lastBeforeId) return "";
    const ddmm = formatDateDDMM(machineRow?.PrevVisitDate);
    return ddmm ? ` (${ddmm})` : "";
  };

  const orderedMachines = useMemo(
    () => sortLocalMachinesFixed(machines, { preferLocalDisplayNumber: true }),
    [machines]
  );

  const buildReadTableWithPrevInline = (type, items, options = {}) => {
    const table = buildVisitTable({
      mode: "read",
      type,
      items,
      helpers: {
        nf: nfRoundedDynamic,
        moneyFmt: moneyFmtRoundedDynamic,
        lastGlobalVisitId: lastLocalVisitBeforeId,
        isCounterVerificationExpanded,
        toggleCounterVerificationExpanded,
      },
      options: { ...options, collectionDetailPresentation },
    });

    const rows = (table.rows || []).map((row, idx) => {
      const sourceItemIndex = Number.isInteger(row?.__sourceItemIndex)
        ? row.__sourceItemIndex
        : idx;
      const m = items?.[sourceItemIndex] || {};
      if (row?.__isCounterVerificationDetailRow === true || row?.__isAdditionalSourceRow === true) {
        return row;
      }
      if (
        isPersistedCollectedMachine(m) ||
        isInsertedInCurrentVisitNoticeRow(m) ||
        type !== "Tragamoneda"
      ) {
        return row;
      }

      const resolvedPreviousPresentation = resolveUncollectedPreviousPresentation(m, type);
      const useAccumulatedMoneyPrevious =
        collectionDetailPresentation &&
        !hasHistoricalRent(m) &&
        type === "Tragamoneda" &&
        m?.IsAccumulative !== false &&
        !hasMachineOperatorFormula(m) &&
        (m?.PrevMoneyIn != null || m?.PrevMoneyOut != null);
      const previousPresentation = useAccumulatedMoneyPrevious
        ? {
            ...resolvedPreviousPresentation,
            useMoney: true,
            inValue: m?.PrevMoneyIn,
            outValue: m?.PrevMoneyOut,
          }
        : resolvedPreviousPresentation;
      const {
        useMoney,
        inValue: inPrev,
        outValue: outPrev,
        label,
      } = previousPresentation;
      const fmt = (v) => formatPrevCollectionValue(v, { useMoney });
      const prevSuffix = getPrevDateSuffix(m);
      const inPrevText = fmt(inPrev);
      const outPrevText = fmt(outPrev);
      const useCollectionUncollectedPresentation =
        collectionDetailPresentation && !hasHistoricalRent(m);
      const detailPreviousLabel =
        useCollectionUncollectedPresentation && prevSuffix
          ? `Anterior${prevSuffix}:`
          : useCollectionUncollectedPresentation
            ? "Anterior:"
            : label;
      const previousValueRow = (value) => (
        <div>
          <div
            style={
              useCollectionUncollectedPresentation
                ? {
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 10,
                    width: "100%",
                  }
                : undefined
            }
          >
            {useCollectionUncollectedPresentation ? (
              <>
                <span>{value === "-" ? "Anterior:" : detailPreviousLabel}</span>
                <span>{value}</span>
              </>
            ) : (
              <>
                {label} {`${value}${value === "-" ? "" : prevSuffix}`}
              </>
            )}
          </div>
        </div>
      );

      return {
        ...row,
        In: previousValueRow(inPrevText),
        Out:
          row?.Out === undefined ? (
            row?.Out
          ) : (
            previousValueRow(outPrevText)
          ),
      };
    });

    let columns = table.columns || [];
    const domainItems = Array.isArray(items)
      ? items.filter((m) => !isInsertedInCurrentVisitNoticeRow(m))
      : [];
    const allNotCollected =
      domainItems.length > 0 && domainItems.every((m) => !isPersistedCollectedMachine(m));
    const allRented =
      Array.isArray(items) && items.length > 0 && items.every((m) => hasHistoricalRent(m));

    if (allNotCollected) {
      const hiddenAccessors = new Set();

      if (allRented) {
        hiddenAccessors.add("In");
      } else if (type === "Rockola") {
        hiddenAccessors.add("In");
      } else if (type === "Pool" || type === "Tejo") {
        hiddenAccessors.add("In");
        hiddenAccessors.add("Total");
      } else if (type === "Peluchera") {
        hiddenAccessors.add("In");
        hiddenAccessors.add("Out");
        hiddenAccessors.add("Total");
      }

      if (hiddenAccessors.size > 0) {
        columns = columns.filter((c) => !hiddenAccessors.has(c?.accessor));
      }
    }

    return { ...table, columns, rows };
  };

  // Agrupar por tipo
  const machinesByType = useMemo(
    () =>
      orderedMachines.reduce((acc, m) => {
        const type = getMachineTypeName(m);
        (acc[type] ||= []).push(m);
        return acc;
      }, {}),
    [orderedMachines]
  );

  const { notRentedByType, rentedByType } = useMemo(() => {
    const NR = {},
      R = {};
    for (const [type, rows] of Object.entries(machinesByType)) {
      const nr = rows.filter((r) => !hasHistoricalRent(r));
      const rr = rows.filter((r) => hasHistoricalRent(r));
      if (nr.length) NR[type] = nr;
      if (rr.length) R[type] = rr;
    }
    return { notRentedByType: NR, rentedByType: R };
  }, [machinesByType]);

  const notRentedEntries = useMemo(() => sortTypeEntries(notRentedByType), [notRentedByType]);

  const rentedEntries = useMemo(() => sortTypeEntries(rentedByType), [rentedByType]);

  const localName = visit?.LocalName ?? visitWithCollection?.LocalName ?? "";
  return (
    <Box mt={4} mb={2}>
      <TypeBlocksGlobalStyles rootId="#visit-details-printable" />

      {/* Wrapper relativo para ubicar el botón flotante */}
      <Box sx={{ position: "relative" }}>
        {/* ====== ÁREA IMPRIMIBLE ====== */}
        <Box id="visit-details-printable">
          {/* Fila superior: barra (izq) + espacio reservado (der) */}
          <Box sx={{ display: "flex", alignItems: "stretch", gap: 1 }}>
            {/* La barra ocupa todo menos el espacio reservado */}
            <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
              <Box
                className="meta-bar"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  rowGap: 0.5,
                  flexWrap: "wrap",
                  px: 1.25,
                  height: META_H,
                  minHeight: META_H,
                  mb: 1.5,
                  background: "rgba(60,80,120,.05)",
                  border: "1px solid rgba(60,80,120,.12)",
                  color: "#4B5563",
                  fontSize: ".95rem",
                  fontWeight: 600,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    flexWrap: "wrap",
                    flex: "1 1 auto",
                    minWidth: 0,
                  }}
                >
                  <span>Detalle de Recaudación</span>
                  <span style={{ opacity: 0.45, userSelect: "none" }}>•</span>
                  <span>
                    Fecha:{" "}
                    <strong style={{ color: "#111827" }}>
                      {collectionDetailPresentation
                        ? formatVisitDateDDMMYYYY(visit)
                        : visit?.DateUY && visit?.TimeUY
                          ? `${visit.DateUY} ${visit.TimeUY}`
                          : visit?.DateTime
                            ? new Date(visit.DateTime).toLocaleString("es-UY", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "—"}
                    </strong>
                  </span>
                  {!collectionDetailPresentation && (
                    <>
                      <span style={{ opacity: 0.45, userSelect: "none" }}>•</span>
                      <span>
                        Visita Id:{" "}
                        <strong style={{ color: "#111827" }}>
                          {visitWithCollection?.Id ?? "—"}
                        </strong>
                      </span>
                    </>
                  )}
                  {localName && (
                    <>
                      <span style={{ opacity: 0.45, userSelect: "none" }}>•</span>
                      <span>
                        Local: <strong style={{ color: "#111827" }}>{localName}</strong>
                      </span>
                    </>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Espaciador del ancho del botón (solo pantalla / no PDF) */}
            <Box className="print-hidden" sx={{ width: 140, flex: "0 0 auto" }} />
          </Box>
          {showPreviousLocalCollectionInfo && (
            <MDTypography variant="button" color="text" sx={{ display: "block", mb: 1.5 }}>
              {lastLocalVisitBeforeDate
                ? `${
                    collectionDetailPresentation
                      ? "Recaudación anterior"
                      : "Última recaudación previa del local"
                  }: ${formatDateDDMMYYYY(lastLocalVisitBeforeDate)}`
                : "No existen recaudaciones anteriores"}
            </MDTypography>
          )}

          {/* ====== BLOQUES NO ALQUILADAS ====== */}
          {notRentedEntries.map(([type, rows]) => {
            const { ClientPercentage, OwnerPercentage } = rows[0] || {};
            const totalsNormal = rows.reduce(
              (acc, m) => ({
                money: acc.money + (Number(m.MoneyCollected) || 0),
                client: acc.client + (Number(m.ClientAmount) || 0),
                owner: acc.owner + (Number(m.OwnerAmount) || 0),
              }),
              { money: 0, client: 0, owner: 0 }
            );
            const typeLabel = machineTypeLabel(type, rows.length);

            return (
              <Box key={`nr-${type}`} mb={2} sx={getTypeStyle(type)} className="vtb">
                <Box sx={{ mb: 0.5 }}>
                  <MDTypography
                    component="span"
                    variant="button"
                    sx={{
                      display: "inline-block",
                      px: 1.25,
                      py: 0.35,
                      borderRadius: CURVE,
                      background: TYPE_COLORS[type]?.border || TYPE_COLORS.Default.border,
                      color: "#fff",
                      lineHeight: 1.15,
                      fontWeight: 700,
                      fontSize: 16,
                    }}
                  >
                    {typeLabel}
                  </MDTypography>
                </Box>

                <Box sx={getInnerPanelStyle(type)} className="vtb-inner">
                  <Box sx={{ mt: 0, ...getTableSkin(type) }}>
                    <DataTable
                      table={buildReadTableWithPrevInline(type, rows, {
                        columnTitles: type === "Rockola" ? { In: "Entrada" } : undefined,
                      })}
                      isSorted={false}
                      entriesPerPage={false}
                      canSearch={false}
                      showTotalEntries={false}
                    />
                  </Box>

                  <Box mt={1}>
                    <MDTypography variant="subtitle2">
                      <strong>Total {typeLabel}:</strong>{" "}
                      <span style={negStyle(totalsNormal.money)}>
                        {moneyFmtRoundedDynamic.format(totalsNormal.money)}
                      </span>
                    </MDTypography>
                    <MDTypography variant="subtitle2">
                      Cliente
                      {ClientPercentage != null
                        ? collectionDetailPresentation
                          ? ` ${formatPercentage(ClientPercentage)} %`
                          : ` ${ClientPercentage}%`
                        : ""}
                      :{" "}
                      <strong style={negStyle(totalsNormal.client)}>
                        {moneyFmtRoundedDynamic.format(totalsNormal.client)}
                      </strong>{" "}
                      – Dueño
                      {OwnerPercentage != null
                        ? collectionDetailPresentation
                          ? ` ${formatPercentage(OwnerPercentage)} %`
                          : ` ${OwnerPercentage}%`
                        : ""}
                      :{" "}
                      <strong style={negStyle(totalsNormal.owner)}>
                        {moneyFmtRoundedDynamic.format(totalsNormal.owner)}
                      </strong>
                    </MDTypography>
                  </Box>
                </Box>
              </Box>
            );
          })}

          {/* ====== BLOQUES ALQUILADAS ====== */}
          {Object.keys(rentedByType).length > 0 &&
            rentedEntries.map(([type, rented]) => {
              const totalRentOwner = rented.reduce(
                (acc, m) => acc + (Number(m.OwnerAmount) || 0),
                0
              );
              const typeLabel = machineTypeLabel(type, rented.length);
              const gender = machineTypeGender(type);
              const rentedTxt = rentedWord(rented.length, gender);

              return (
                <Box key={`r-${type}`} mb={2} sx={getTypeStyle(type)} className="vtb">
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      borderRadius: CURVE,
                      overflow: "hidden",
                      mb: 0.5,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        px: 1.25,
                        py: 0.35,
                        background: TYPE_COLORS[type]?.border || TYPE_COLORS.Default.border,
                        color: "#fff",
                        fontWeight: 700,
                        lineHeight: 1.15,
                        fontSize: 16,
                      }}
                    >
                      {typeLabel}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        px: 1.25,
                        py: 0.35,
                        background: TYPE_COLORS.Alquilada.border,
                        color: "#fff",
                        fontWeight: 700,
                        lineHeight: 1.15,
                        fontSize: 16,
                      }}
                    >
                      {rentedTxt}
                    </Box>
                  </Box>

                  <Box sx={getInnerPanelStyle(type)} className="vtb-inner">
                    <Box sx={{ ...getTableSkin(type) }}>
                      <DataTable
                        table={buildReadTableWithPrevInline(type, rented, { hideTotal: true })}
                        isSorted={false}
                        entriesPerPage={false}
                        canSearch={false}
                        showTotalEntries={false}
                      />
                    </Box>

                    <Box mt={1}>
                      <MDTypography variant="subtitle2">
                        <strong>Total {typeLabel}:</strong>{" "}
                        <span style={negStyle(totalRentOwner)}>
                          {moneyFmtRoundedDynamic.format(totalRentOwner)}
                        </span>
                      </MDTypography>
                    </Box>
                  </Box>
                </Box>
              );
            })}

          {/* ====== TOTALES FINALES (estilo barra meta) ====== */}
          <Box
            className="meta-totals"
            sx={{
              mt: 2,
              mb: 2,
              px: 1.25,
              py: 1.2,
              borderRadius: CURVE,
              background: "rgba(60,80,120,.05)",
              border: "1px solid rgba(60,80,120,.12)",
              display: "flex",
              flexWrap: "wrap",
              alignItems: collectionDetailPresentation ? "flex-start" : "center",
              justifyContent: collectionDetailPresentation ? "flex-start" : "space-between",
              flexDirection: collectionDetailPresentation ? "column" : "row",
              gap: 1,
            }}
          >
            <MDTypography variant="subtitle1" sx={{ m: 0 }}>
              <strong>Total:</strong>{" "}
              <span style={negStyle(visitWithCollection?.TotalAmount)}>
                {moneyFmtRoundedDynamic.format(visitWithCollection?.TotalAmount ?? 0)}
              </span>
            </MDTypography>
            <MDTypography variant="subtitle1" sx={{ m: 0 }}>
              <strong>Cliente:</strong>{" "}
              <span style={negStyle(visitWithCollection?.TotalClientAmount)}>
                {moneyFmtRoundedDynamic.format(visitWithCollection?.TotalClientAmount ?? 0)}
              </span>{" "}
              <strong>{collectionDetailPresentation ? "– Dueño:" : "/ Dueño:"}</strong>{" "}
              <span style={negStyle(visitWithCollection?.TotalOwnerAmount)}>
                {moneyFmtRoundedDynamic.format(visitWithCollection?.TotalOwnerAmount ?? 0)}
              </span>
            </MDTypography>
          </Box>
        </Box>

        {/* ====== BOTÓN (fuera del printable, no aparece en PDF) ====== */}
        <Box sx={shareFloaterSx} className="print-hidden">
          <ShareButton
            className="print-hidden"
            sx={{
              height: META_H,
              minHeight: META_H,
              paddingInline: 16,
              borderRadius: `${CURVE}px !important`,
            }}
            disabled={!a4DataReady || isGeneratingA4}
            onClick={async () => {
              if (!a4DataReady || isGeneratingA4) return;
              setIsGeneratingA4(true);
              try {
                const model = buildCollectionA4Model({
                  visit,
                  visitWithCollection,
                  local,
                  person,
                  machines: orderedMachines,
                  lastLocalVisitBeforeId,
                  lastLocalVisitBeforeDate,
                  showPreviousLocalCollectionInfo,
                });
                await generateCollectionA4Pdf(model, {
                  filename: buildVisitPdfFilename({
                    date: visit?.Date,
                    localName: visit?.LocalName ?? visitWithCollection?.LocalName ?? "",
                  }),
                });
              } catch (error) {
                if (error?.name !== "AbortError") {
                  if (process.env.NODE_ENV !== "production") console.error(error);
                  notifyError("No se pudo generar el PDF A4.");
                }
              } finally {
                setIsGeneratingA4(false);
              }
            }}
          >
            {isGeneratingA4 ? "Generando..." : "Compartir"}
          </ShareButton>
        </Box>
      </Box>
    </Box>
  );
}

VisitTransactionDetails.propTypes = {
  visit: PropTypes.shape({
    Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    LocalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    LocalName: PropTypes.string,
  }),
  visitWithCollection: PropTypes.shape({
    Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    TotalAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    TotalClientAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    TotalOwnerAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  machines: PropTypes.arrayOf(PropTypes.object),
  lastLocalVisitBeforeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lastLocalVisitBeforeDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showPreviousLocalCollectionInfo: PropTypes.bool,
  local: PropTypes.object,
  person: PropTypes.object,
  a4DataReady: PropTypes.bool,
  collectionDetailPresentation: PropTypes.bool,
};

export default React.memo(VisitTransactionDetails);
